import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook générique pour les requêtes Supabase en lecture.
 *
 * Remplace le pattern fetch/loading/error copié-collé dans chaque page.
 * L'appelant garde le contrôle total de la requête via queryFn.
 *
 * @param queryFn Fonction retournant une promesse Supabase { data, error }
 * @param deps Tableau de dépendances pour le re-fetch automatique (comme useEffect)
 *
 * @example
 * const { data: employees, loading, refresh } = useQuery(
 *   () => supabase.from('employees').select('*').eq('is_active', true),
 *   []
 * );
 */
export function useQuery<T = any>(
  queryFn: () => PromiseLike<{ data: T[] | null; error: any }>,
  deps: any[] = []
): {
  data: T[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
} {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Ref pour éviter les updates sur un composant démonté
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: result, error: queryError } = await queryFn();
      if (!mountedRef.current) return;
      if (queryError) throw queryError;
      setData(result ?? []);
    } catch (err) {
      if (!mountedRef.current) return;
      const e = err instanceof Error ? err : new Error(
        typeof err === 'object' && err !== null && 'message' in err ? (err as any).message : String(err)
      );
      console.error('[useQuery]', e.message);
      setError(e);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}
